import { Icons } from '@commenda/kakashi';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { ChatRoomMessageType, IncorporationRequestStatus } from '@prisma/client';
import { useMemo } from 'react';
import { useChatMessages } from '../../../hooks/useChatMessages';

interface AgentRejectWarningBannerProps {
  workRequestData?: WorkRequestResponseType;
}

const AgentRejectWarningBanner = ({ workRequestData }: AgentRejectWarningBannerProps) => {
  const { messages } = useChatMessages(workRequestData);

  const hasIssueReported = useMemo(() => {
    return messages.some((msg) => msg.type === ChatRoomMessageType.ISSUE_REPORTED);
  }, [messages]);

  const isApprovedAfterRejection = useMemo(() => {
    if (!hasIssueReported || !workRequestData?.status) return false;
    
    const status = workRequestData.status as IncorporationRequestStatus;
    return (
      status === IncorporationRequestStatus.Filed ||
      status === IncorporationRequestStatus.Completed
    );
  }, [hasIssueReported, workRequestData?.status]);

  if (!hasIssueReported) {
    return null;
  }

  // Green banner if form has been approved after rejection
  if (isApprovedAfterRejection) {
    return (
      <div className="bg-green-50 border-b border-green-200 px-6 py-3">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <Icons.CheckCircle className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-sm text-gray-700">
            Great! Your agent has reviewed and approved your submission. Your incorporation request is now being processed.
          </p>
        </div>
      </div>
    );
  }

  const status = workRequestData?.status;
  if (status === 'Submitted') {
    return null;
  }

  // Red banner if there are agent remarks
  return (
    <div className="bg-red-50 border-b border-red-200 px-6 py-3">
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">
          <Icons.AlertCircle className="w-5 h-5 text-red-500" />
        </div>
        <p className="text-sm text-gray-700">
          Your agent has rejected your submission due to certain issues. Please make suitable corrections and resubmit this form.
        </p>
      </div>
    </div>
  );
};

export default AgentRejectWarningBanner;
